import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  AppBar,
  Toolbar,
  IconButton,
  Card,

  Avatar,
  Chip,

  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  ArrowBack,
  Storage,
  Favorite,
  Star,
  People,
  SwipeRight,
  PersonAdd,
  Delete,
  Undo
} from '@mui/icons-material';
import axios from 'axios';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`firebase-tabpanel-${index}`}
      aria-labelledby={`firebase-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const FirebaseDataPage: React.FC = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Data states
  const [cafeFavorites, setCafeFavorites] = useState([]);
  const [cafeRatings, setCafeRatings] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);
  const [friendships, setFriendships] = useState([]);
  const [userSwipes, setUserSwipes] = useState([]);
  const [userFavorites, setUserFavorites] = useState([]);
  const [undoLimits, setUndoLimits] = useState([]);
  const [deletedAccounts, setDeletedAccounts] = useState([]);
  
  // Cache to track loaded data
  const [loadedTabs, setLoadedTabs] = useState<Set<number>>(new Set());

  const fetchData = async (endpoint: string, setter: Function) => {
    try {
      setLoading(true);
      setError(''); // Clear previous errors
      const token = localStorage.getItem('adminToken');

      // Deleted accounts için limit'i artır ve daha uzun timeout
      const params = endpoint === 'deleted-accounts' ? { limit: 1000 } : {};
      const timeoutDuration = endpoint === 'deleted-accounts' ? 60000 : 10000; // 60 saniye deleted-accounts için

      const response = await axios.get(`https://api.sobcof.com/api/${endpoint}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        params,
        timeout: timeoutDuration
      });

      if (response.data.success) {
        setter(response.data.data);
      } else {
        setError(`${endpoint} verisi alınamadı`);
      }
    } catch (error: any) {
      console.error(`Error fetching ${endpoint}:`, error);
      if (error.code === 'ECONNABORTED') {
        setError(`${endpoint} verisi yüklenirken zaman aşımı oluştu. Lütfen daha sonra tekrar deneyin.`);
      } else if (error.response?.status === 401) {
        setError('Oturum süreniz dolmuş. Lütfen tekrar giriş yapın.');
      } else if (error.response?.status === 500) {
        setError(`Sunucu hatası: ${endpoint} verisi alınamadı`);
      } else {
        setError(`${endpoint} verisi alınamadı: ${error.message || 'Bilinmeyen hata'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch data if not already loaded
    if (loadedTabs.has(tabValue)) {
      return;
    }

    // Fetch data based on active tab
    switch (tabValue) {
      case 0:
        fetchData('cafe-favorites', setCafeFavorites);
        break;
      case 1:
        fetchData('cafe-ratings', setCafeRatings);
        break;
      case 2:
        fetchData('friend-requests', setFriendRequests);
        break;
      case 3:
        fetchData('friendships', setFriendships);
        break;
      case 4:
        fetchData('user-swipes', setUserSwipes);
        break;
      case 5:
        fetchData('user-favorites', setUserFavorites);
        break;
      case 6:
        fetchData('user-undo-limits', setUndoLimits);
        break;
      case 7:
        fetchData('deleted-accounts', setDeletedAccounts);
        break;
    }
    
    // Mark tab as loaded
    setLoadedTabs(prev => new Set(prev).add(tabValue));
  }, [tabValue, loadedTabs]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setError('');
  };

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: '#f8fafc' }}>
      {/* App Bar */}
      <AppBar 
        position="static" 
        sx={{
          background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)',
          boxShadow: '0 4px 20px rgba(139, 69, 19, 0.3)'
        }}
      >
        <Toolbar sx={{ minHeight: 70 }}>
          <IconButton color="inherit" onClick={() => navigate('/admin/dashboard')} size="large">
            <ArrowBack />
          </IconButton>
          <Avatar sx={{ 
            bgcolor: 'rgba(255,255,255,0.2)', 
            mr: 2,
            width: 40,
            height: 40
          }}>
            <Storage sx={{ fontSize: 20 }} />
          </Avatar>
          <Typography variant="h5" component="div" sx={{ 
            flexGrow: 1,
            fontWeight: 600,
            color: 'white'
          }}>
            Firebase Verileri
          </Typography>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Tabs */}
        <Card elevation={0} sx={{ 
          borderRadius: 3,
          border: '1px solid #e2e8f0',
          overflow: 'hidden',
          mb: 3
        }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: '1px solid #e2e8f0' }}
          >
            <Tab icon={<Favorite />} label="Cafe Favorileri" />
            <Tab icon={<Star />} label="Cafe Değerlendirmeleri" />
            <Tab icon={<PersonAdd />} label="Arkadaşlık İstekleri" />
            <Tab icon={<People />} label="Arkadaşlıklar" />
            <Tab icon={<SwipeRight />} label="Kullanıcı Swipe'ları" />
            <Tab icon={<Favorite />} label="Kullanıcı Favorileri" />
            <Tab icon={<Undo />} label="Geri Alma Limitleri" />
            <Tab icon={<Delete />} label="Silinmiş Hesaplar" />
          </Tabs>

          {/* Tab Panels */}
          <TabPanel value={tabValue} index={0}>
            <Typography variant="h6" sx={{ mb: 2 }}>Cafe Favorileri</Typography>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Cafe</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Favori Sayısı</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Son Güncelleme</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cafeFavorites.map((favorite: any) => (
                      <TableRow key={favorite.id}>
                        <TableCell>{favorite.cafeName}</TableCell>
                        <TableCell>
                          <Chip 
                            label={favorite.count} 
                            color="primary" 
                            size="small"
                            icon={<Favorite />}
                          />
                        </TableCell>
                        <TableCell>
                          {new Date(favorite.lastUpdated).toLocaleDateString('tr-TR')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Typography variant="h6" sx={{ mb: 2 }}>Cafe Değerlendirmeleri</Typography>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Kullanıcı</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Cafe</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Puan</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Tarih</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cafeRatings.map((rating: any) => (
                      <TableRow key={rating.id}>
                        <TableCell>{rating.userName}</TableCell>
                        <TableCell>{rating.cafeName}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Star sx={{ color: '#ffc107', fontSize: 16 }} />
                            <Typography variant="body2">{rating.rating}/5</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {new Date(rating.createdAt).toLocaleDateString('tr-TR')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Typography variant="h6" sx={{ mb: 2 }}>Arkadaşlık İstekleri</Typography>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Gönderen</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Alan</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Durum</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Mesaj</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Tarih</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {friendRequests.map((request: any) => (
                      <TableRow key={request.id}>
                        <TableCell>{request.senderName}</TableCell>
                        <TableCell>{request.receiverName}</TableCell>
                        <TableCell>
                          <Chip 
                            label={
                              request.status === 'pending' ? 'Bekliyor' :
                              request.status === 'accepted' ? 'Kabul Edildi' :
                              request.status === 'rejected' ? 'Reddedildi' : request.status
                            }
                            color={
                              request.status === 'pending' ? 'warning' :
                              request.status === 'accepted' ? 'success' :
                              request.status === 'rejected' ? 'error' : 'default'
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 150 }}>
                          <Typography variant="body2" noWrap>
                            {request.message || 'Mesaj yok'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {new Date(request.createdAt).toLocaleDateString('tr-TR')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>

          {/* Arkadaşlıklar Tab */}
          <TabPanel value={tabValue} index={3}>
            <Typography variant="h6" sx={{ mb: 2 }}>Arkadaşlıklar</Typography>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Kullanıcı 1</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Kullanıcı 2</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Durum</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Oluşturulma Tarihi</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {friendships.map((friendship: any) => (
                      <TableRow key={friendship.id}>
                        <TableCell>{friendship.user1Name}</TableCell>
                        <TableCell>{friendship.user2Name}</TableCell>
                        <TableCell>
                          <Chip
                            label={friendship.status === 'active' ? 'Aktif' : 'Pasif'}
                            color={friendship.status === 'active' ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {new Date(friendship.createdAt).toLocaleDateString('tr-TR')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>

          {/* Kullanıcı Swipe'ları Tab */}
          <TabPanel value={tabValue} index={4}>
            <Typography variant="h6" sx={{ mb: 2 }}>Kullanıcı Swipe'ları</Typography>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Kullanıcı</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Hedef Kullanıcı</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Swipe Türü</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Tarih</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {userSwipes.map((swipe: any) => (
                      <TableRow key={swipe.id}>
                        <TableCell>{swipe.userName}</TableCell>
                        <TableCell>{swipe.targetUserName}</TableCell>
                        <TableCell>
                          <Chip
                            label={
                              swipe.swipeType === 'like' ? 'Beğeni' :
                              swipe.swipeType === 'pass' ? 'Geç' :
                              swipe.swipeType === 'super_like' ? 'Süper Beğeni' : swipe.swipeType
                            }
                            color={
                              swipe.swipeType === 'like' ? 'success' :
                              swipe.swipeType === 'super_like' ? 'primary' : 'default'
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {new Date(swipe.createdAt).toLocaleDateString('tr-TR')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>

          {/* Kullanıcı Favorileri Tab */}
          <TabPanel value={tabValue} index={5}>
            <Typography variant="h6" sx={{ mb: 2 }}>Kullanıcı Favorileri</Typography>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Kullanıcı</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Cafe</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Durum</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Oluşturulma Tarihi</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {userFavorites.map((favorite: any) => (
                      <TableRow key={favorite.id}>
                        <TableCell>{favorite.userName}</TableCell>
                        <TableCell>{favorite.cafeName}</TableCell>
                        <TableCell>
                          <Chip
                            label={favorite.isActive ? 'Aktif' : 'Pasif'}
                            color={favorite.isActive ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {new Date(favorite.createdAt).toLocaleDateString('tr-TR')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>

          {/* Geri Alma Limitleri Tab */}
          <TabPanel value={tabValue} index={6}>
            <Typography variant="h6" sx={{ mb: 2 }}>Geri Alma Limitleri</Typography>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Kullanıcı</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Kalan Geri Alma</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Maksimum</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Son Sıfırlama</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {undoLimits.map((limit: any) => (
                      <TableRow key={limit.id}>
                        <TableCell>{limit.userName}</TableCell>
                        <TableCell>
                          <Chip
                            label={limit.remainingUndos}
                            color={limit.remainingUndos > 0 ? 'success' : 'error'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{limit.maxUndos}</TableCell>
                        <TableCell>
                          {new Date(limit.lastReset).toLocaleDateString('tr-TR')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>

          {/* Silinmiş Hesaplar Tab */}
          <TabPanel value={tabValue} index={7}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">Silinmiş Hesaplar</Typography>
              <Chip
                label={`Toplam: ${deletedAccounts.length}`}
                color="error"
                sx={{ fontWeight: 600 }}
              />
            </Box>

            {/* Stats Cards */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <Card sx={{ flex: 1, bgcolor: '#fff3e0', borderRadius: 2 }}>
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#e65100' }}>
                    {deletedAccounts.filter((a: any) => a.canRestore).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Geri Yüklenebilir
                  </Typography>
                </Box>
              </Card>
              <Card sx={{ flex: 1, bgcolor: '#ffebee', borderRadius: 2 }}>
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#c62828' }}>
                    {deletedAccounts.filter((a: any) => !a.canRestore).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Kalıcı Silindi
                  </Typography>
                </Box>
              </Card>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Kullanıcı</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Silme Nedeni</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Silinme Tarihi</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Geri Yüklenebilir</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {deletedAccounts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                            Silinmiş hesap bulunamadı
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      deletedAccounts.map((account: any) => (
                        <TableRow key={account.id}>
                          <TableCell>{account.userName}</TableCell>
                          <TableCell>{account.email}</TableCell>
                          <TableCell>{account.reason}</TableCell>
                          <TableCell>
                            {new Date(account.deletedAt).toLocaleDateString('tr-TR')}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={account.canRestore ? 'Evet' : 'Hayır'}
                              color={account.canRestore ? 'success' : 'error'}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>
        </Card>
      </Box>
    </Box>
  );
};

export default FirebaseDataPage;
